import { createContext, useContext, useState, ReactNode } from 'react'
import { ChevronDown, ChevronUp, Dog, Cat, PawPrint } from 'lucide-react'

export type StatusType = 'success' | 'warning' | 'error' | 'info' | 'neutral'
export type SpeciesType = 'perro' | 'gato' | 'otro'

interface StatusCardContextValue {
  status: StatusType
  expanded: boolean
  toggleExpanded: () => void
}

const StatusCardContext = createContext<StatusCardContextValue | null>(null)

function useStatusCardContext() {
  const context = useContext(StatusCardContext)
  if (!context) {
    throw new Error('StatusCard compound components must be used within StatusCard')
  }
  return context
}

function getSpeciesIcon(species?: SpeciesType) {
  if (species === 'perro') return <Dog size={24} />
  if (species === 'gato') return <Cat size={24} />
  return <PawPrint size={24} />
}

export interface StatusCardProps {
  children: ReactNode
  status?: StatusType
  stripe?: boolean
  stripeIcon?: SpeciesType
  expandable?: boolean
  expanded?: boolean
  defaultExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  selectable?: boolean
  selected?: boolean
  onSelect?: () => void
  className?: string
  id?: string
}

export function StatusCard({
  children,
  status = 'neutral',
  stripe = false,
  stripeIcon,
  expandable = false,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onExpandedChange,
  selectable = false,
  selected = false,
  onSelect,
  className = '',
  id,
}: StatusCardProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)

  const isControlled = controlledExpanded !== undefined
  const expanded = isControlled ? controlledExpanded : internalExpanded

  const toggleExpanded = () => {
    if (!expandable) return
    const newValue = !expanded
    if (!isControlled) {
      setInternalExpanded(newValue)
    }
    onExpandedChange?.(newValue)
  }

  const handleClick = () => {
    if (selectable && onSelect) {
      onSelect()
    } else if (expandable) {
      toggleExpanded()
    }
  }

  const isInteractive = expandable || selectable
  const hasStripeIcon = stripe && stripeIcon

  return (
    <StatusCardContext.Provider value={{ status, expanded, toggleExpanded }}>
      <div
        id={id}
        className={`
          status-card
          status-card--${status}
          ${stripe ? 'status-card--stripe' : ''}
          ${hasStripeIcon ? 'status-card--stripe-icon' : ''}
          ${expandable ? 'status-card--expandable' : ''}
          ${expanded ? 'status-card--expanded' : ''}
          ${selectable ? 'status-card--selectable' : ''}
          ${selected ? 'status-card--selected' : ''}
          ${className}
        `.trim().replace(/\s+/g, ' ')}
        onClick={isInteractive ? handleClick : undefined}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onKeyDown={
          isInteractive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleClick()
                }
              }
            : undefined
        }
        aria-expanded={expandable ? expanded : undefined}
      >
        {hasStripeIcon && (
          <div className={`status-card__stripe status-card__stripe--${stripeIcon}`}>
            <div className="status-card__stripe-icon">
              {getSpeciesIcon(stripeIcon)}
            </div>
          </div>
        )}
        <div className={hasStripeIcon ? 'status-card__body' : undefined}>
          {children}
        </div>
      </div>
    </StatusCardContext.Provider>
  )
}

interface StatusCardHeaderProps {
  children: ReactNode
  className?: string
}

function StatusCardHeader({ children, className = '' }: StatusCardHeaderProps) {
  const { expanded, toggleExpanded } = useStatusCardContext()

  return (
    <div className={`status-card__header ${className}`}>
      {children}
      <button
        className="status-card__expand-btn"
        onClick={(e) => {
          e.stopPropagation()
          toggleExpanded()
        }}
        aria-label={expanded ? 'Colapsar' : 'Expandir'}
      >
        {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
    </div>
  )
}

interface StatusCardTitleProps {
  children: ReactNode
  className?: string
}

function StatusCardTitle({ children, className = '' }: StatusCardTitleProps) {
  return <h3 className={`status-card__title ${className}`}>{children}</h3>
}

interface StatusCardSubtitleProps {
  children: ReactNode
  className?: string
}

function StatusCardSubtitle({ children, className = '' }: StatusCardSubtitleProps) {
  return <p className={`status-card__subtitle ${className}`}>{children}</p>
}

interface StatusCardContentProps {
  children: ReactNode
  className?: string
  collapsible?: boolean
}

function StatusCardContent({ children, className = '', collapsible = true }: StatusCardContentProps) {
  const { expanded } = useStatusCardContext()

  if (collapsible && !expanded) {
    return null
  }

  return <div className={`status-card__content ${className}`}>{children}</div>
}

interface StatusCardActionsProps {
  children: ReactNode
  className?: string
}

function StatusCardActions({ children, className = '' }: StatusCardActionsProps) {
  return <div className={`status-card__actions ${className}`}>{children}</div>
}

StatusCard.Header = StatusCardHeader
StatusCard.Title = StatusCardTitle
StatusCard.Subtitle = StatusCardSubtitle
StatusCard.Content = StatusCardContent
StatusCard.Actions = StatusCardActions
